import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { labelClass, inputClass } from '@/components/ui/inputStyles';
import { LabeledToggleSwitch } from '@/components/ui/LabeledToggleSwitch';
import { generateWesWebhookSecret } from '@/lib/webhookSecret';
import { buildRobotSiteDisplayRows, type RobotSiteRow } from '@/lib/robotSiteTree';

type MpResp = {
  ok: boolean;
  miniprogram?: {
    id: number;
    name: string;
    site_ids: number[];
    entry_site_options?: Array<{ id: number; site_name: string }>;
    android_robot_api_key?: string;
    types?: Array<{ key: string; label: string }>;
  };
};

type RobotInheritMeta = {
  parent_site_id?: number | null;
  parent_site_name?: string;
  robot_inherit_parent?: boolean;
  using_parent_minipro_settings?: boolean;
};

type RobotSettingsSource =
  | 'miniprogram_override'
  | 'miniprogram_inherit_parent'
  | 'miniprogram_default'
  | 'site'
  | string;

type RobotSummary = {
  site_id: number;
  parent_site_id?: number | null;
  robot_module_enabled: boolean;
  forward_strategy: 'auto' | 'curated';
  forward_daily_cap: number;
  /** oldest_first=入队早先执行；newest_first=最近入队先执行 */
  forward_queue_order?: 'oldest_first' | 'newest_first';
  /** null=全部信息类型自动转发 */
  forward_type_keys?: string[] | null;
  forward_webhook_enabled?: boolean;
  forward_webhook_submit_url?: string;
  forward_webhook_source_label?: string;
  forward_webhook_receive_enabled?: boolean;
  forward_webhook_receive_show_in_mp?: boolean;
  forward_webhook_receive_key?: string;
  forward_webhook_receive_url?: string;
  forward_task_count: number;
  forward_task_count_aggregate?: number;
  inherit?: RobotInheritMeta;
  robot_settings_source?: RobotSettingsSource;
  entry_info_webhook_enabled?: boolean;
  entry_info_webhook_url?: string;
};

type BatchSummaryResp = { ok: boolean; items: RobotSummary[] };

export function RobotMiniprogramDetailPage() {
  const { mpId: mpParam } = useParams();
  const mpId = Math.max(0, parseInt(mpParam || '0', 10) || 0);

  const [mpName, setMpName] = useState('');
  const [sites, setSites] = useState<RobotSiteRow[]>([]);
  const [androidKey, setAndroidKey] = useState('');
  const [keySaving, setKeySaving] = useState(false);
  const [keyMsg, setKeyMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [summaryById, setSummaryById] = useState<Record<number, RobotSummary>>({});
  const [strategySite, setStrategySite] = useState<RobotSiteRow | null>(null);
  const [strategyChoice, setStrategyChoice] = useState<'auto' | 'curated'>('curated');
  const [strategyDailyCap, setStrategyDailyCap] = useState(10);
  const [strategyQueueOrder, setStrategyQueueOrder] = useState<'oldest_first' | 'newest_first'>('oldest_first');
  const [strategyForwardAllTypes, setStrategyForwardAllTypes] = useState(true);
  const [strategyForwardTypeKeys, setStrategyForwardTypeKeys] = useState<string[]>([]);
  const [mpTypes, setMpTypes] = useState<Array<{ key: string; label: string }>>([]);
  const [strategySaving, setStrategySaving] = useState(false);
  const [strategyWebhookEnabled, setStrategyWebhookEnabled] = useState(false);
  const [strategyWebhookSubmitUrl, setStrategyWebhookSubmitUrl] = useState('');
  const [strategyWebhookSourceLabel, setStrategyWebhookSourceLabel] = useState('贴图虾');
  const [strategyReceiveEnabled, setStrategyReceiveEnabled] = useState(false);
  const [strategyReceiveShowInMp, setStrategyReceiveShowInMp] = useState(false);
  const [strategyReceiveUrl, setStrategyReceiveUrl] = useState('');
  const [strategyReceiveKey, setStrategyReceiveKey] = useState('');
  const [webhookTesting, setWebhookTesting] = useState(false);
  const [webhookTestMsg, setWebhookTestMsg] = useState('');
  const [receiveCopyMsg, setReceiveCopyMsg] = useState('');
  const [infoPushSite, setInfoPushSite] = useState<RobotSiteRow | null>(null);
  const [infoPushEnabled, setInfoPushEnabled] = useState(false);
  const [infoPushUrl, setInfoPushUrl] = useState('');
  const [infoPushSaving, setInfoPushSaving] = useState(false);
  const [infoPushTesting, setInfoPushTesting] = useState(false);
  const [infoPushMsg, setInfoPushMsg] = useState('');

  const load = useCallback(async () => {
    if (mpId < 1) return;
    setLoading(true);
    setMsg('');
    try {
      const r = await apiFetch<MpResp>('get_miniprogram', { method: 'GET', query: { id: mpId } });
      if (!r.ok || !r.miniprogram) {
        setMsg('无法加载小程序');
        return;
      }
      const m = r.miniprogram;
      setMpName(m.name);
      setAndroidKey(String(m.android_robot_api_key ?? ''));
      setMpTypes(Array.isArray(m.types) ? m.types.filter((t) => t && t.key) : []);

      const opts = Array.isArray(m.entry_site_options) ? m.entry_site_options : [];
      const scopeIds = new Set<number>(
        opts.length > 0
          ? opts.map((o) => Number(o.id)).filter((x) => x > 0)
          : (m.site_ids || []).map((x) => Number(x)).filter((x) => x > 0),
      );

      const ls = await apiFetch<{ ok: boolean; items: RobotSiteRow[] }>('list_sites', {
        method: 'GET',
        query: { page: 1, per_page: 500, only_roots: 0 },
      });
      let filtered: RobotSiteRow[] = [];
      if (ls.ok) {
        filtered = ls.items.filter((s) => scopeIds.has(s.id));
        const have = new Set(filtered.map((s) => s.id));
        for (const o of opts) {
          const oid = Number(o.id);
          if (oid > 0 && !have.has(oid)) {
            filtered.push({
              id: oid,
              site_code: '',
              site_name: String(o.site_name ?? `站点 #${oid}`),
              parent_site_id: null,
            });
          }
        }
      } else if (opts.length > 0) {
        filtered = opts.map((o) => ({
          id: Number(o.id),
          site_code: '',
          site_name: String(o.site_name ?? `站点 #${o.id}`),
          parent_site_id: null,
        }));
      }
      setSites(filtered);

      const ids = filtered.map((s) => s.id);
      if (ids.length === 0) {
        setSummaryById({});
        return;
      }
      const b = await apiFetch<BatchSummaryResp>('batch_robot_site_summary', {
        method: 'POST',
        body: JSON.stringify({ site_ids: ids, miniprogram_id: mpId }),
      });
      if (!b.ok) {
        setMsg('加载站点机器人概览失败');
        return;
      }
      const map: Record<number, RobotSummary> = {};
      for (const it of b.items) {
        const cap =
          typeof it.forward_daily_cap === 'number' && !Number.isNaN(it.forward_daily_cap)
            ? Math.min(500, Math.max(1, Math.round(it.forward_daily_cap)))
            : 10;
        const qOrder = it.forward_queue_order === 'newest_first' ? 'newest_first' : 'oldest_first';
        map[it.site_id] = { ...it, forward_daily_cap: cap, forward_queue_order: qOrder };
      }
      setSummaryById(map);
    } finally {
      setLoading(false);
    }
  }, [mpId]);

  useEffect(() => {
    void load();
  }, [load]);

  const siteDisplayRows = useMemo(() => buildRobotSiteDisplayRows(sites), [sites]);

  function fillRandomAndroidRobotKey() {
    setAndroidKey(generateWesWebhookSecret());
    setKeyMsg('已填入随机密钥，请点击「保存密钥」写入服务端');
  }

  async function saveAndroidKey() {
    if (mpId < 1) return;
    setKeySaving(true);
    setKeyMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_miniprogram_android_robot_key', {
        method: 'POST',
        body: JSON.stringify({ miniprogram_id: mpId, android_robot_api_key: androidKey }),
      });
      setKeyMsg(r.ok ? '已保存' : r.err || '保存失败');
      if (r.ok) await load();
    } finally {
      setKeySaving(false);
    }
  }

  async function toggleModule(siteId: number, enabled: boolean) {
    setMsg('');
    const cur = summaryById[siteId];
    const r = await apiFetch<{ ok: boolean; err?: string }>('save_miniprogram_site_robot_settings', {
      method: 'POST',
      body: JSON.stringify({
        miniprogram_id: mpId,
        site_id: siteId,
        robot_module_enabled: enabled,
        forward_strategy: cur?.forward_strategy ?? 'curated',
        forward_daily_cap: cur?.forward_daily_cap ?? 10,
        forward_queue_order: cur?.forward_queue_order === 'newest_first' ? 'newest_first' : 'oldest_first',
        forward_type_keys: cur?.forward_type_keys ?? null,
        forward_webhook_enabled: cur?.forward_webhook_enabled ?? false,
        forward_webhook_submit_url: cur?.forward_webhook_submit_url ?? '',
        forward_webhook_source_label: cur?.forward_webhook_source_label ?? '贴图虾',
      }),
    });
    if (!r.ok) {
      setMsg(r.err || '保存失败');
      return;
    }
    await load();
  }

  function openStrategy(s: RobotSiteRow) {
    const cur = summaryById[s.id]?.forward_strategy ?? 'curated';
    setStrategyChoice(cur === 'auto' ? 'auto' : 'curated');
    const cap = summaryById[s.id]?.forward_daily_cap;
    setStrategyDailyCap(typeof cap === 'number' && !Number.isNaN(cap) ? Math.min(500, Math.max(1, Math.round(cap))) : 10);
    setStrategyQueueOrder(summaryById[s.id]?.forward_queue_order === 'newest_first' ? 'newest_first' : 'oldest_first');
    const fwdKeys = summaryById[s.id]?.forward_type_keys;
    if (fwdKeys === null || fwdKeys === undefined) {
      setStrategyForwardAllTypes(true);
      setStrategyForwardTypeKeys([]);
    } else {
      setStrategyForwardAllTypes(false);
      setStrategyForwardTypeKeys(Array.isArray(fwdKeys) ? [...fwdKeys] : []);
    }
    const sum = summaryById[s.id];
    setStrategyWebhookEnabled(!!sum?.forward_webhook_enabled);
    setStrategyWebhookSubmitUrl(String(sum?.forward_webhook_submit_url ?? ''));
    setStrategyWebhookSourceLabel(String(sum?.forward_webhook_source_label ?? '贴图虾') || '贴图虾');
    setStrategyReceiveEnabled(!!sum?.forward_webhook_receive_enabled);
    setStrategyReceiveShowInMp(!!sum?.forward_webhook_receive_show_in_mp);
    setStrategyReceiveUrl(String(sum?.forward_webhook_receive_url ?? ''));
    setStrategyReceiveKey(String(sum?.forward_webhook_receive_key ?? ''));
    setWebhookTestMsg('');
    setReceiveCopyMsg('');
    setStrategySite(s);
  }

  function openInfoPush(s: RobotSiteRow) {
    const sum = summaryById[s.id];
    setInfoPushEnabled(!!sum?.entry_info_webhook_enabled);
    setInfoPushUrl(String(sum?.entry_info_webhook_url ?? ''));
    setInfoPushMsg('');
    setInfoPushSite(s);
  }

  async function saveInfoPush() {
    if (!infoPushSite) return;
    if (infoPushEnabled && !infoPushUrl.trim().startsWith('https://')) {
      setInfoPushMsg('开启时须填写以 https:// 开头的完整 Webhook 地址');
      return;
    }
    setInfoPushSaving(true);
    setInfoPushMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('save_entry_info_webhook', {
        method: 'POST',
        body: JSON.stringify({
          miniprogram_id: mpId,
          site_id: infoPushSite.id,
          enabled: infoPushEnabled,
          url: infoPushUrl.trim(),
        }),
      });
      if (!r.ok) {
        setInfoPushMsg(r.detail || r.err || '保存失败');
        return;
      }
      setInfoPushSite(null);
      await load();
    } catch (e) {
      setInfoPushMsg(e instanceof Error ? e.message : '保存失败');
    } finally {
      setInfoPushSaving(false);
    }
  }

  async function testInfoPush() {
    if (!infoPushSite) return;
    if (!infoPushUrl.trim().startsWith('https://')) {
      setInfoPushMsg('请先填写以 https:// 开头的完整 Webhook 地址');
      return;
    }
    setInfoPushTesting(true);
    setInfoPushMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string; http_code?: number; entry_id?: number }>(
        'test_entry_info_webhook',
        {
          method: 'POST',
          body: JSON.stringify({
            miniprogram_id: mpId,
            site_id: infoPushSite.id,
            url: infoPushUrl.trim(),
          }),
        },
      );
      if (!r.ok) {
        setInfoPushMsg(r.detail || r.err || '测试推送失败');
        return;
      }
      const idPart = r.entry_id ? `（信息 #${r.entry_id}）` : '';
      setInfoPushMsg(`推送成功${idPart}${r.detail ? `：${r.detail}` : ''}`);
    } catch (e) {
      setInfoPushMsg(e instanceof Error ? e.message : '测试推送失败');
    } finally {
      setInfoPushTesting(false);
    }
  }

  function toggleStrategyForwardType(key: string) {
    setStrategyForwardAllTypes(false);
    setStrategyForwardTypeKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }

  function setStrategyForwardAllTypesEnabled(all: boolean) {
    setStrategyForwardAllTypes(all);
    if (all) {
      setStrategyForwardTypeKeys([]);
    }
  }

  async function saveStrategy() {
    if (!strategySite) return;
    if (strategyWebhookEnabled && !strategyWebhookSubmitUrl.trim().startsWith('https://')) {
      setMsg('启用 Webhook 推送时须填写以 https:// 开头的完整入队地址');
      return;
    }
    if (strategyChoice === 'auto' && !strategyForwardAllTypes && strategyForwardTypeKeys.length === 0) {
      setMsg('自动转发模式下须选择至少一种信息类型，或勾选「全部信息类型」');
      return;
    }
    setStrategySaving(true);
    setMsg('');
    try {
      const r = await apiFetch<{
        ok: boolean;
        err?: string;
        detail?: string;
        forward_webhook_receive_enabled?: boolean;
        forward_webhook_receive_show_in_mp?: boolean;
        forward_webhook_receive_key?: string;
        forward_webhook_receive_url?: string;
      }>('save_miniprogram_site_robot_settings', {
        method: 'POST',
        body: JSON.stringify({
          miniprogram_id: mpId,
          site_id: strategySite.id,
          forward_strategy: strategyChoice,
          forward_daily_cap: Math.min(500, Math.max(1, Math.round(strategyDailyCap))),
          forward_queue_order: strategyQueueOrder,
          forward_type_keys: strategyForwardAllTypes ? null : strategyForwardTypeKeys,
          forward_webhook_enabled: strategyWebhookEnabled,
          forward_webhook_submit_url: strategyWebhookSubmitUrl.trim(),
          forward_webhook_source_label: strategyWebhookSourceLabel.trim() || '贴图虾',
          forward_webhook_receive_enabled: strategyReceiveEnabled,
          forward_webhook_receive_show_in_mp: strategyReceiveShowInMp,
          // 接收开关已即时落库；此处仅在开启时顺带固化密钥，避免被空串冲掉
          ...(strategyReceiveEnabled && strategyReceiveKey.trim()
            ? { forward_webhook_receive_key: strategyReceiveKey.trim() }
            : {}),
        }),
      });
      if (!r.ok) {
        setMsg(
          r.err === 'forward_webhook_submit_url_required'
            ? '启用 Webhook 推送时须填写完整入队地址（含 tenant_slug 与 webhook_key）'
            : r.err === 'forward_type_keys_empty'
              ? '自动转发模式下须选择至少一种信息类型，或勾选「全部信息类型」'
              : r.detail || r.err || '保存失败',
        );
        return;
      }
      // 接收配置已即时保存，整单保存仍关闭弹窗（与其它策略字段一致）
      setStrategySite(null);
      await load();
    } finally {
      setStrategySaving(false);
    }
  }

  async function persistReceiveToggle(enabled: boolean) {
    if (!strategySite) return;
    setStrategySaving(true);
    setReceiveCopyMsg('');
    setMsg('');
    try {
      const r = await apiFetch<{
        ok: boolean;
        err?: string;
        detail?: string;
        forward_webhook_receive_enabled?: boolean;
        forward_webhook_receive_show_in_mp?: boolean;
        forward_webhook_receive_key?: string;
        forward_webhook_receive_url?: string;
      }>('save_miniprogram_site_robot_settings', {
        method: 'POST',
        body: JSON.stringify({
          miniprogram_id: mpId,
          site_id: strategySite.id,
          forward_webhook_receive_enabled: enabled,
          // 开启时：有密钥则保留，无则后端自动生成；关闭时不必传 key
          ...(enabled
            ? { forward_webhook_receive_key: strategyReceiveKey.trim() || '' }
            : {}),
        }),
      });
      if (!r.ok) {
        setStrategyReceiveEnabled(!enabled);
        setReceiveCopyMsg(r.detail || r.err || '保存失败');
        return;
      }
      const on = !!r.forward_webhook_receive_enabled;
      const showInMp = !!r.forward_webhook_receive_show_in_mp;
      const key = String(r.forward_webhook_receive_key ?? '');
      const url = String(r.forward_webhook_receive_url ?? '');
      setStrategyReceiveEnabled(on);
      setStrategyReceiveShowInMp(showInMp);
      setStrategyReceiveKey(key);
      setStrategyReceiveUrl(url);
      setSummaryById((prev) => {
        const cur = prev[strategySite.id];
        if (!cur) return prev;
        return {
          ...prev,
          [strategySite.id]: {
            ...cur,
            forward_webhook_receive_enabled: on,
            forward_webhook_receive_show_in_mp: showInMp,
            forward_webhook_receive_key: key,
            forward_webhook_receive_url: url,
            robot_module_enabled: on ? true : cur.robot_module_enabled,
          },
        };
      });
      setReceiveCopyMsg(on ? (url ? '已开启并生成接收链接' : '已开启（请检查站点 site_url 配置）') : '已关闭接收');
    } catch (ex) {
      setStrategyReceiveEnabled(!enabled);
      setReceiveCopyMsg(ex instanceof Error ? ex.message : '保存失败');
    } finally {
      setStrategySaving(false);
    }
  }

  async function persistReceiveShowInMpToggle(enabled: boolean) {
    if (!strategySite) return;
    setStrategySaving(true);
    setReceiveCopyMsg('');
    setMsg('');
    try {
      const r = await apiFetch<{
        ok: boolean;
        err?: string;
        detail?: string;
        forward_webhook_receive_show_in_mp?: boolean;
      }>('save_miniprogram_site_robot_settings', {
        method: 'POST',
        body: JSON.stringify({
          miniprogram_id: mpId,
          site_id: strategySite.id,
          forward_webhook_receive_show_in_mp: enabled,
        }),
      });
      if (!r.ok) {
        setStrategyReceiveShowInMp(!enabled);
        setReceiveCopyMsg(r.detail || r.err || '保存失败');
        return;
      }
      const on = !!r.forward_webhook_receive_show_in_mp;
      setStrategyReceiveShowInMp(on);
      setSummaryById((prev) => {
        const cur = prev[strategySite.id];
        if (!cur) return prev;
        return {
          ...prev,
          [strategySite.id]: {
            ...cur,
            forward_webhook_receive_show_in_mp: on,
          },
        };
      });
      setReceiveCopyMsg(on ? '已开启：Webhook 任务将在微信小程序端可见' : '已关闭：Webhook 任务仅入转发队列，小程序端不可见');
    } catch (ex) {
      setStrategyReceiveShowInMp(!enabled);
      setReceiveCopyMsg(ex instanceof Error ? ex.message : '保存失败');
    } finally {
      setStrategySaving(false);
    }
  }

  async function copyReceiveUrl() {
    const url = strategyReceiveUrl.trim();
    if (!url) {
      setReceiveCopyMsg('暂无接收链接，请先开启开关');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setReceiveCopyMsg('已复制接收链接');
    } catch {
      setReceiveCopyMsg('复制失败，请手动选中复制');
    }
  }

  async function regenerateReceiveKey() {
    if (!strategySite) return;
    if (!window.confirm('重新生成密钥后，旧接收链接将立即失效，小蜜蜂等系统需更新为新链接。确定？')) {
      return;
    }
    setStrategySaving(true);
    setReceiveCopyMsg('');
    try {
      const r = await apiFetch<{
        ok: boolean;
        err?: string;
        detail?: string;
        forward_webhook_receive_enabled?: boolean;
        forward_webhook_receive_key?: string;
        forward_webhook_receive_url?: string;
      }>('save_miniprogram_site_robot_settings', {
        method: 'POST',
        body: JSON.stringify({
          miniprogram_id: mpId,
          site_id: strategySite.id,
          forward_webhook_receive_enabled: true,
          forward_webhook_receive_key: '', // 后端视为重新生成
        }),
      });
      if (!r.ok) {
        setReceiveCopyMsg(r.detail || r.err || '重新生成失败');
        return;
      }
      const key = String(r.forward_webhook_receive_key ?? '');
      const url = String(r.forward_webhook_receive_url ?? '');
      setStrategyReceiveEnabled(true);
      setStrategyReceiveKey(key);
      setStrategyReceiveUrl(url);
      setSummaryById((prev) => {
        const cur = prev[strategySite.id];
        if (!cur) return prev;
        return {
          ...prev,
          [strategySite.id]: {
            ...cur,
            forward_webhook_receive_enabled: true,
            forward_webhook_receive_key: key,
            forward_webhook_receive_url: url,
            robot_module_enabled: true,
          },
        };
      });
      setReceiveCopyMsg(url ? '已生成新密钥与链接' : '已生成新密钥（链接为空，请检查 site_url）');
    } catch (ex) {
      setReceiveCopyMsg(ex instanceof Error ? ex.message : '重新生成失败');
    } finally {
      setStrategySaving(false);
    }
  }

  async function testWebhookPush() {
    if (!strategySite) return;
    if (!strategyWebhookEnabled) {
      setWebhookTestMsg('请先开启 Webhook 推送');
      return;
    }
    if (!strategyWebhookSubmitUrl.trim().startsWith('https://')) {
      setWebhookTestMsg('请先填写有效的 Webhook 入队地址');
      return;
    }
    setWebhookTesting(true);
    setWebhookTestMsg('');
    try {
      const tasks = await apiFetch<{ ok: boolean; items?: Array<{ entry_id?: number }> }>('list_forward_tasks', {
        method: 'GET',
        query: { site_id: strategySite.id, miniprogram_id: mpId, page: 1, per_page: 1 },
      });
      const entryId = tasks.items?.[0]?.entry_id;
      if (!tasks.ok || !entryId) {
        setWebhookTestMsg('请先在转发队列中添加至少一条信息，或保存配置后再试');
        return;
      }
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string; duplicate?: boolean; http_code?: number }>(
        'test_forward_webhook_push',
        {
          method: 'POST',
          body: JSON.stringify({
            miniprogram_id: mpId,
            site_id: strategySite.id,
            entry_id: entryId,
          }),
        },
      );
      if (!r.ok) {
        setWebhookTestMsg(r.detail || r.err || '测试推送失败');
        return;
      }
      setWebhookTestMsg(
        r.duplicate
          ? `连通正常（HTTP ${r.http_code ?? 200}）：目标系统已存在相同 external_id，视为幂等成功`
          : `推送成功（HTTP ${r.http_code ?? 200}），请在小蜜蜂「转发任务」中查看 Webhook 来源任务`,
      );
    } finally {
      setWebhookTesting(false);
    }
  }

  async function restoreRobotInherit(siteId: number) {
    setMsg('');
    const r = await apiFetch<{ ok: boolean; err?: string }>('restore_site_settings_parent_inherit', {
      method: 'POST',
      body: JSON.stringify({ site_id: siteId, scope: 'robot' }),
    });
    if (!r.ok) {
      setMsg(r.err || '恢复继承失败');
      return;
    }
    setStrategySite(null);
    await load();
  }

  if (mpId < 1) {
    return <p className="text-sm text-rose-700">无效的小程序</p>;
  }

  const strategyInherit = strategySite ? summaryById[strategySite.id]?.inherit : undefined;
  const strategySiteSummary = strategySite ? summaryById[strategySite.id] : undefined;
  const strategyRobotOn = !!strategySiteSummary?.robot_module_enabled;
  const strategyIsAuto = strategyChoice === 'auto';

  return (
    <div className="space-y-6">
      <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-slate-500">
        <Link to="/miniprograms" className="rounded-md hover:bg-slate-100 hover:text-teal-800">
          小程序管理
        </Link>
        <span className="text-slate-300" aria-hidden>
          /
        </span>
        <span className="font-medium text-slate-800">{mpName || `小程序 #${mpId}`}</span>
      </nav>

      <PageHeader
        title={mpName ? `站点转发配置 · ${mpName}` : '站点转发配置'}
        description={`小程序 #${mpId}：各站点的转发开关、策略与队列均仅在本小程序内生效；同一站点在其他小程序中需单独配置。二级站点可继承本小程序内一级站点的策略。`}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to={`/robot-management?mp_id=${mpId}`}>
              <Button type="button" variant="primary">
                机器人管理
              </Button>
            </Link>
            <Button type="button" variant="secondary" onClick={() => void load()} disabled={loading}>
              刷新
            </Button>
            <Link to="/miniprograms">
              <Button type="button" variant="secondary">
                返回小程序管理
              </Button>
            </Link>
          </div>
        }
      />
      {msg ? <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">{msg}</p> : null}

      <Card title="小程序 Android 机器人密钥">
        <p className="text-sm text-slate-600">
          Android 端拉取转发任务、上报完成状态时需携带此密钥。留空则拒绝机器人接口请求；建议定期更换并同步到客户端配置。
        </p>
        {keyMsg ? <p className="mt-2 text-sm text-slate-700">{keyMsg}</p> : null}
        <div className="mt-3 flex max-w-xl flex-col gap-2 sm:flex-row sm:items-end">
          <label className={`${labelClass} min-w-0 flex-1`}>
            <span>密钥（明文仅在此处展示）</span>
            <input
              className={`${inputClass} font-mono text-sm`}
              value={androidKey}
              onChange={(e) => setAndroidKey(e.target.value)}
              placeholder="留空表示未配置"
              spellCheck={false}
              autoComplete="off"
              maxLength={255}
              disabled={keySaving}
            />
          </label>
          <Button
            type="button"
            variant="secondary"
            className="shrink-0 sm:mb-0.5"
            disabled={keySaving}
            onClick={fillRandomAndroidRobotKey}
          >
            生成随机密钥
          </Button>
        </div>
        <div className="mt-3">
          <Button type="button" variant="primary" disabled={keySaving} onClick={() => void saveAndroidKey()}>
            {keySaving ? '保存中…' : '保存密钥'}
          </Button>
        </div>
      </Card>

      <Card title="站点（转发策略 / 队列）">
        <p className="mb-3 text-sm text-slate-600">
          以下配置仅保存在<strong>本小程序</strong>，与站点管理中的全局机器人设置、以及其他小程序互不影响。未单独配置的站点默认为「转发关闭 + 管理员挑选」。一级站点可查看本小程序下聚合队列（含下属二级）。
        </p>
        {loading ? (
          <Spinner label="加载中" />
        ) : sites.length === 0 ? (
          <p className="text-sm text-amber-800">
            当前小程序未关联站点，或站点不在可见范围内。请先在「站点管理」中创建/关联站点后再配置机器人。
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs text-slate-500">
                  <th className="py-2 pr-4 font-medium">站点</th>
                  <th className="py-2 pr-4 font-medium">模块</th>
                  <th className="py-2 pr-4 font-medium">策略</th>
                  <th className="py-2 pr-4 font-medium">转发类型</th>
                  <th className="py-2 pr-4 font-medium">队列</th>
                  <th className="py-2 text-right font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {siteDisplayRows.map(({ site: s, depth, subline }) => {
                  const sum = summaryById[s.id];
                  const on = sum?.robot_module_enabled ?? false;
                  const strat = sum?.forward_strategy ?? 'curated';
                  const dailyCap = typeof sum?.forward_daily_cap === 'number' ? sum.forward_daily_cap : 10;
                  const directQ = sum?.forward_task_count ?? 0;
                  const aggQ = sum?.forward_task_count_aggregate ?? directQ;
                  const inheriting = !!sum?.inherit?.using_parent_minipro_settings;
                  const parentLabel = sum?.inherit?.parent_site_name?.trim();
                  const src = sum?.robot_settings_source ?? 'miniprogram_default';
                  const isDefault = src === 'miniprogram_default';

                  return (
                    <tr
                      key={s.id}
                      className={`border-b border-slate-100 ${depth === 1 ? 'bg-emerald-50/30' : ''}`}
                    >
                      <td className={`py-2 pr-4 ${depth === 1 ? 'border-l-2 border-l-emerald-400/80 pl-6' : ''}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          {depth === 0 ? (
                            <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                              一级
                            </span>
                          ) : (
                            <span className="rounded bg-emerald-700 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                              二级
                            </span>
                          )}
                          <div>
                            <div className="font-medium text-slate-900">{s.site_name}</div>
                            <div className="text-xs text-slate-500">
                              #{s.id}
                              {s.site_code ? ` · ${s.site_code}` : ''}
                            </div>
                            {subline ? <p className="mt-0.5 text-[11px] text-emerald-800/90">{subline}</p> : null}
                            {isDefault ? (
                              <p className="mt-0.5 text-[11px] text-slate-500">本小程序未单独配置（默认关闭转发）</p>
                            ) : inheriting && parentLabel ? (
                              <p className="mt-0.5 text-[11px] text-sky-800">
                                沿用本小程序内一级站「{parentLabel}」的策略
                              </p>
                            ) : depth === 1 && !inheriting ? (
                              <p className="mt-0.5 text-[11px] text-emerald-800">本小程序内已单独配置</p>
                            ) : src === 'miniprogram_override' ? (
                              <p className="mt-0.5 text-[11px] text-emerald-800">本小程序内已单独配置</p>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 pr-4">
                        {on ? (
                          <span className="text-emerald-700">转发已开启</span>
                        ) : (
                          <span className="text-slate-500">转发已关闭</span>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <span>{strat === 'auto' ? '新信息自动转发' : '管理员挑选转发'}</span>
                        <span className="mt-0.5 block text-xs text-slate-500">每日上限 {dailyCap} 条/站点</span>
                        <span className="mt-0.5 block text-xs text-slate-500">
                          {sum?.forward_queue_order === 'newest_first' ? '最近入队先执行' : '入队早的先执行'}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-xs text-slate-600">
                        {(() => {
                          const fwdKeys = sum?.forward_type_keys;
                          if (fwdKeys === null || fwdKeys === undefined) {
                            return <span className="text-slate-700">全部类型</span>;
                          }
                          if (!fwdKeys.length) {
                            return <span className="text-amber-800">不自动转发任何类型</span>;
                          }
                          const labels = fwdKeys.map((k) => {
                            const hit = mpTypes.find((t) => t.key === k);
                            return hit?.label || k;
                          });
                          return (
                            <span title={labels.join('、')}>
                              {labels.slice(0, 2).join('、')}
                              {labels.length > 2 ? ` 等 ${labels.length} 类` : ''}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-2 pr-4">
                        {depth === 0 && aggQ > directQ ? (
                          <>
                            <span className="font-medium text-slate-800">{aggQ}</span>
                            <span className="text-slate-600"> 条</span>
                            <span className="mt-0.5 block text-xs text-slate-500">
                              本站 {directQ} + 二级 {aggQ - directQ}
                            </span>
                          </>
                        ) : (
                          <span>{directQ} 条</span>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        <div className="flex flex-wrap justify-end gap-1.5">
                          <Button
                            type="button"
                            variant={summaryById[s.id]?.entry_info_webhook_enabled ? 'primary' : 'secondary'}
                            className="!px-2 !py-1 text-xs"
                            onClick={() => openInfoPush(s)}
                          >
                            新信息推送
                          </Button>
                          <Button type="button" variant="secondary" className="!px-2 !py-1 text-xs" onClick={() => openStrategy(s)}>
                            转发策略
                          </Button>
                          <Link to={`/robot-miniprograms/${mpId}/sites/${s.id}/tasks`}>
                            <Button type="button" variant="secondary" className="!px-2 !py-1 text-xs">
                              转发任务
                            </Button>
                          </Link>
                          <Button
                            type="button"
                            variant={on ? 'danger' : 'primary'}
                            className="!px-2 !py-1 text-xs"
                            onClick={() => void toggleModule(s.id, !on)}
                          >
                            {on ? '关闭转发' : '开启转发'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {strategySite ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">
              转发策略 · {strategySite.site_name}
              {strategySite.parent_site_id ? (
                <span className="ml-1 text-sm font-normal text-emerald-700">（二级站点）</span>
              ) : null}
            </h3>
            {strategyInherit?.using_parent_minipro_settings && strategyInherit.parent_site_name ? (
              <p className="mt-2 rounded-lg border border-sky-200/90 bg-sky-50 px-3 py-2 text-xs text-sky-950">
                当前沿用本小程序内一级站「{strategyInherit.parent_site_name}」的策略。保存后将为本站在本小程序内的独立配置。
              </p>
            ) : strategySite.parent_site_id && strategyInherit?.parent_site_name ? (
              <p className="mt-2 rounded-lg border border-amber-200/90 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                本站已在本小程序内单独配置（不再沿用「{strategyInherit.parent_site_name}」）。
              </p>
            ) : (
              <p className="mt-1 text-xs text-slate-500">
                仅影响<strong>本小程序</strong>下该站点的转发行为；与其他小程序无关。
              </p>
            )}
            {strategySiteSummary ? (
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                <p>
                  <span className="font-medium text-slate-800">入队前提：</span>
                  转发模块
                  <span className={strategyRobotOn ? 'text-emerald-700' : 'text-amber-800'}>
                    {strategyRobotOn ? '已开启' : '未开启'}
                  </span>
                  · 策略
                  <span className="font-medium text-slate-800">{strategyIsAuto ? '自动转发' : '管理员挑选'}</span>
                  {strategyIsAuto ? (
                    <>
                      {' '}
                      · 类型
                      {strategyForwardAllTypes
                        ? '全部'
                        : strategyForwardTypeKeys.length > 0
                          ? `已选 ${strategyForwardTypeKeys.length} 种`
                          : '未选（将阻止自动入队）'}
                    </>
                  ) : null}
                </p>
                {!strategyRobotOn && strategyIsAuto ? (
                  <p className="mt-1 text-amber-900">保存「自动转发」后将自动开启本站转发模块。</p>
                ) : null}
                {!strategyIsAuto ? (
                  <p className="mt-1 text-slate-600">
                    「管理员挑选」模式下，新信息不会自动进入转发任务，须手动添加；Webhook 推送也仅在入队后触发。
                  </p>
                ) : null}
              </div>
            ) : null}
            <div className="mt-4 space-y-3">
              <label className={`${labelClass} flex cursor-pointer items-start gap-2`}>
                <input type="radio" name="fwd" checked={strategyChoice === 'auto'} onChange={() => setStrategyChoice('auto')} className="mt-1" />
                <span>
                  <span className="font-medium text-slate-800">新信息自动转发</span>
                  <span className="mt-0.5 block text-xs text-slate-500">新登记信息在满足条件时自动进入转发队列（受每日上限约束）。</span>
                </span>
              </label>
              <label className={`${labelClass} flex cursor-pointer items-start gap-2`}>
                <input type="radio" name="fwd" checked={strategyChoice === 'curated'} onChange={() => setStrategyChoice('curated')} className="mt-1" />
                <span>
                  <span className="font-medium text-slate-800">管理员挑选转发</span>
                  <span className="mt-0.5 block text-xs text-slate-500">仅手动加入转发队列的信息会被 Android 机器人拉取。</span>
                </span>
              </label>
              <label className={labelClass}>
                <span>每日转发上限（条）</span>
                <input
                  type="number"
                  min={1}
                  max={500}
                  className={inputClass}
                  value={strategyDailyCap}
                  onChange={(e) => setStrategyDailyCap(Math.min(500, Math.max(1, parseInt(e.target.value, 10) || 10)))}
                />
                <span className="mt-1 block text-xs text-slate-500">
                  自动转发模式下，单站点每日最多入队条数；管理员挑选模式不受此限用于手动入队。默认 10。
                </span>
              </label>
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-800">转发执行顺序</p>
                <p className="text-xs text-slate-500">机器人拉取本站小程序转发任务时的先后顺序（按入队时间）。</p>
                <label className={`${labelClass} flex cursor-pointer items-start gap-2`}>
                  <input
                    type="radio"
                    name="fwd_queue_order"
                    checked={strategyQueueOrder === 'oldest_first'}
                    onChange={() => setStrategyQueueOrder('oldest_first')}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-medium text-slate-800">入队早的先执行</span>
                    <span className="mt-0.5 block text-xs text-slate-500">默认。先入转发队列的任务优先下发。</span>
                  </span>
                </label>
                <label className={`${labelClass} flex cursor-pointer items-start gap-2`}>
                  <input
                    type="radio"
                    name="fwd_queue_order"
                    checked={strategyQueueOrder === 'newest_first'}
                    onChange={() => setStrategyQueueOrder('newest_first')}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-medium text-slate-800">最近入队的先执行</span>
                    <span className="mt-0.5 block text-xs text-slate-500">优先处理最新加入队列的任务。</span>
                  </span>
                </label>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                <p className="text-sm font-medium text-slate-800">转发信息类型</p>
                <p className="mt-1 text-xs text-slate-500">
                  仅影响「新信息自动转发」的入队范围；未勾选的类型不会自动进入转发队列，但可在转发任务页手动添加。
                </p>
                <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={strategyForwardAllTypes}
                    onChange={(e) => setStrategyForwardAllTypesEnabled(e.target.checked)}
                  />
                  <span>全部信息类型</span>
                </label>
                {mpTypes.length > 0 ? (
                  <div className={`mt-2 flex flex-wrap gap-2 ${strategyForwardAllTypes ? 'opacity-50' : ''}`}>
                    {mpTypes.map((t) => {
                      const checked = strategyForwardTypeKeys.includes(t.key);
                      return (
                        <label
                          key={t.key}
                          className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition ${
                            checked && !strategyForwardAllTypes
                              ? 'border-teal-500 bg-teal-50 text-teal-900'
                              : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            disabled={strategyForwardAllTypes}
                            checked={checked}
                            onChange={() => toggleStrategyForwardType(t.key)}
                          />
                          <span>{t.label}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-amber-800">请先在「小程序管理 → 信息类型」中配置类型后再筛选。</p>
                )}
                {!strategyForwardAllTypes && strategyForwardTypeKeys.length === 0 ? (
                  <p className="mt-2 text-xs text-amber-800">未选择任何类型时，自动转发将不会入队任何信息（仍可手动添加）。</p>
                ) : null}
              </div>

              <div className="rounded-xl border border-sky-200/80 bg-sky-50/40 p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-800">第三方 Webhook 任务接收</p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      接收小蜜蜂等系统推送的转发任务，进入本站点队列（与下方「推送」方向相反）。默认不在微信小程序端展示。
                    </p>
                  </div>
                  <LabeledToggleSwitch
                    checked={strategyReceiveEnabled}
                    onChange={(on) => {
                      setStrategyReceiveEnabled(on);
                      void persistReceiveToggle(on);
                    }}
                    disabled={strategySaving}
                    busy={strategySaving}
                    aria-label="第三方 Webhook 任务接收"
                  />
                </div>
                {strategyReceiveEnabled ? (
                  <div className="mt-3 space-y-3 border-t border-sky-200/60 pt-3">
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-sky-100 bg-white/70 px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800">在微信小程序端展示</p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          关闭（默认）：仅加入转发任务，不出现在小程序列表/详情。开启后小程序端可查询到这些信息。
                        </p>
                      </div>
                      <LabeledToggleSwitch
                        checked={strategyReceiveShowInMp}
                        onChange={(on) => {
                          setStrategyReceiveShowInMp(on);
                          void persistReceiveShowInMpToggle(on);
                        }}
                        disabled={strategySaving}
                        busy={strategySaving}
                        aria-label="在微信小程序端展示"
                      />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-700">接收任务的 Webhook 链接</p>
                      <div className="mt-1.5 flex flex-col gap-2 sm:flex-row sm:items-stretch">
                        <input
                          className={`${inputClass} min-w-0 flex-1 font-mono text-xs`}
                          value={
                            strategyReceiveUrl ||
                            (strategySaving
                              ? '正在生成…'
                              : '开启后自动生成完整链接')
                          }
                          readOnly
                          spellCheck={false}
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={strategySaving || !strategyReceiveUrl}
                          onClick={() => void copyReceiveUrl()}
                        >
                          复制
                        </Button>
                      </div>
                      <p className="mt-1.5 text-xs text-slate-500">
                        开启后立即生效并生成链接，可直接复制到小蜜蜂「智能定时推送 → 第三方 Webhook 推送」。
                      </p>
                    </div>
                    {receiveCopyMsg ? (
                      <p className="rounded-lg border border-sky-200 bg-white/80 px-3 py-2 text-xs text-sky-900">
                        {receiveCopyMsg}
                      </p>
                    ) : null}
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={strategySaving}
                      onClick={() => void regenerateReceiveKey()}
                    >
                      重新生成密钥
                    </Button>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-slate-500">关闭时拒绝外部入队请求。</p>
                )}
              </div>

              <div className="rounded-xl border border-violet-200/80 bg-violet-50/40 p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-800">第三方 Webhook 推送</p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      转发任务入队时，同步 POST 到小蜜蜂等系统的 Webhook 入队接口，由其转发机器人执行。
                    </p>
                  </div>
                  <LabeledToggleSwitch
                    checked={strategyWebhookEnabled}
                    onChange={setStrategyWebhookEnabled}
                    disabled={strategySaving}
                    busy={strategySaving}
                    aria-label="第三方 Webhook 推送"
                  />
                </div>

                {strategyWebhookEnabled ? (
                  <div className="mt-3 space-y-3 border-t border-violet-200/60 pt-3">
                    <label className={labelClass}>
                      <span>Webhook 完整入队地址</span>
                      <input
                        className={`${inputClass} font-mono text-xs`}
                        value={strategyWebhookSubmitUrl}
                        onChange={(e) => setStrategyWebhookSubmitUrl(e.target.value)}
                        placeholder="https://autosy.example.com/api/api.php?action=forward_webhook_submit&tenant_slug=…&webhook_key=…"
                        spellCheck={false}
                        autoComplete="off"
                        disabled={strategySaving}
                      />
                      <span className="mt-1 block text-xs text-slate-500">
                        从小蜜蜂「转发 Webhook 接口」页复制<strong>专属完整调用地址</strong>（须含 tenant_slug 与 webhook_key）。
                      </span>
                    </label>
                    <label className={labelClass}>
                      <span>来源标识（source_label）</span>
                      <input
                        className={inputClass}
                        value={strategyWebhookSourceLabel}
                        onChange={(e) => setStrategyWebhookSourceLabel(e.target.value)}
                        placeholder="贴图虾"
                        maxLength={64}
                        disabled={strategySaving}
                      />
                      <span className="mt-1 block text-xs text-slate-500">
                        小蜜蜂转发任务列表中显示的任务来源；保存后会自动附加站点名称。
                      </span>
                    </label>
                    {webhookTestMsg ? (
                      <p
                        className={`rounded-lg border px-3 py-2 text-xs ${
                          webhookTestMsg.includes('成功') || webhookTestMsg.includes('连通')
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                            : 'border-amber-200 bg-amber-50 text-amber-950'
                        }`}
                      >
                        {webhookTestMsg}
                      </p>
                    ) : null}
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={strategySaving || webhookTesting}
                      onClick={() => void testWebhookPush()}
                    >
                      {webhookTesting ? '测试中…' : '测试连通（使用队列最新一条）'}
                    </Button>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-slate-500">
                    不影响贴图虾本地 Android 机器人拉取；仅额外推送到第三方系统。
                  </p>
                )}
              </div>
            </div>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              {strategySite.parent_site_id && !strategyInherit?.robot_inherit_parent ? (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={strategySaving}
                  onClick={() => void restoreRobotInherit(strategySite.id)}
                >
                  恢复继承一级
                </Button>
              ) : null}
              <Button type="button" variant="secondary" onClick={() => setStrategySite(null)} disabled={strategySaving}>
                取消
              </Button>
              <Button type="button" variant="primary" onClick={() => void saveStrategy()} disabled={strategySaving}>
                {strategySaving ? '保存中…' : '保存'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {infoPushSite ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">新信息推送 · {infoPushSite.site_name}</h3>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">
              仅作用于本小程序下的该站点，不沿用一级站点，也与转发队列无关。有新信息发布（免审上线或审核通过）时，向该地址 POST 一次
              JSON：<span className="font-mono">{'{ text, images }'}</span>
              。正文为标题、描述、电话和微信号；图片为公网 https 地址，最多 9 张。待审、编辑、刷新不会推送，同一条只推一次。
            </p>
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-slate-800">有新信息时推送</p>
                <LabeledToggleSwitch
                  checked={infoPushEnabled}
                  onChange={setInfoPushEnabled}
                  disabled={infoPushSaving || infoPushTesting}
                  aria-label="有新信息时推送"
                />
              </div>
              <label className={labelClass}>
                <span>Webhook 地址</span>
                <input
                  className={`${inputClass} font-mono text-xs`}
                  value={infoPushUrl}
                  onChange={(e) => setInfoPushUrl(e.target.value)}
                  placeholder="https://"
                  spellCheck={false}
                  autoComplete="off"
                  disabled={infoPushSaving || infoPushTesting}
                />
                <span className="mt-1 block text-xs text-slate-500">
                  填写对方提供的完整接收地址（含密钥参数）。推送失败只记日志，不影响本站发布。
                </span>
              </label>
              {infoPushMsg ? (
                <p
                  className={`rounded-lg border px-3 py-2 text-xs ${
                    infoPushMsg.includes('成功')
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                      : 'border-amber-200 bg-amber-50 text-amber-950'
                  }`}
                >
                  {infoPushMsg}
                </p>
              ) : null}
              <p className="text-xs text-slate-500">
                测试推送会用该站最近一条已发布信息再发一次，对方可能因此多入库一条，且不占用「只推一次」的名额。
              </p>
            </div>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={infoPushSaving || infoPushTesting}
                onClick={() => void testInfoPush()}
              >
                {infoPushTesting ? '测试中…' : '测试推送'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={infoPushSaving || infoPushTesting}
                onClick={() => setInfoPushSite(null)}
              >
                取消
              </Button>
              <Button type="button" variant="primary" disabled={infoPushSaving || infoPushTesting} onClick={() => void saveInfoPush()}>
                {infoPushSaving ? '保存中…' : '保存'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
